import { useState, useEffect } from 'react';
import { situacoesExemplo, dicasSwish, checklistEuIdeal, perguntasGatilho } from '../../../mocks/worksheet-swish';

interface PadraoSwish {
  id: number;
  comportamento: string;
  gatilho: string;
  imagemIndesejada: {
    descricao: string;
    tamanho: string;
    nitidez: string;
    proximidade: string;
  };
  imagemIdeal: {
    descricao: string;
    checklist: number[];
  };
  repeticoes: number;
  testado: boolean;
  resultado: string;
}

export default function PraticaSwish() {
  const [padroes, setPadroes] = useState<PadraoSwish[]>([]);
  const [padraoAtual, setPadraoAtual] = useState(0);
  const [showExemplos, setShowExemplos] = useState(false);
  const [dicaAleatoria, setDicaAleatoria] = useState('');
  const [contadorSwish, setContadorSwish] = useState(0);
  const [animandoSwish, setAnimandoSwish] = useState(false);

  useEffect(() => {
    // Inicializa com um padrão vazio
    if (padroes.length === 0) {
      adicionarNovoPadrao();
    }
    // Dica aleatória
    setDicaAleatoria(dicasSwish[Math.floor(Math.random() * dicasSwish.length)]);
  }, []);

  const adicionarNovoPadrao = () => {
    if (padroes.length >= 5) return;
    
    const novoPadrao: PadraoSwish = {
      id: Date.now(),
      comportamento: '',
      gatilho: '',
      imagemIndesejada: {
        descricao: '',
        tamanho: 'grande',
        nitidez: 'nitida',
        proximidade: 'proxima'
      },
      imagemIdeal: {
        descricao: '',
        checklist: []
      },
      repeticoes: 0,
      testado: false,
      resultado: ''
    };
    
    setPadroes([...padroes, novoPadrao]);
    setPadraoAtual(padroes.length);
  };

  const atualizarPadrao = (campo: string, valor: any) => {
    const novospadroes = [...padroes];
    const padrao = novospadroes[padraoAtual];
    
    if (campo.includes('.')) {
      const [obj, prop] = campo.split('.');
      padrao[obj as keyof PadraoSwish][prop] = valor;
    } else {
      padrao[campo as keyof PadraoSwish] = valor;
    }
    
    setPadroes(novospadroes);
  };

  const aplicarExemplo = (exemplo: typeof situacoesExemplo[0]) => {
    atualizarPadrao('comportamento', exemplo.comportamento);
    atualizarPadrao('gatilho', exemplo.gatilho);
    atualizarPadrao('imagemIndesejada.descricao', exemplo.imagemIndesejada);
    atualizarPadrao('imagemIdeal.descricao', exemplo.imagemIdeal);
    setShowExemplos(false);
  };

  const executarSwish = () => {
    if (contadorSwish >= 10) return;
    
    setAnimandoSwish(true);
    setContadorSwish(contadorSwish + 1);
    atualizarPadrao('repeticoes', contadorSwish + 1);
    
    setTimeout(() => {
      setAnimandoSwish(false);
    }, 800);
  };

  const resetarSwish = () => {
    setContadorSwish(0);
    atualizarPadrao('repeticoes', 0);
  };

  const toggleChecklistItem = (itemId: number) => {
    const padrao = padroes[padraoAtual];
    const checklist = padrao.imagemIdeal.checklist || [];
    
    if (checklist.includes(itemId)) {
      atualizarPadrao('imagemIdeal.checklist', checklist.filter(id => id !== itemId));
    } else {
      atualizarPadrao('imagemIdeal.checklist', [...checklist, itemId]);
    }
  };

  const calcularProgresso = () => {
    const padrao = padroes[padraoAtual];
    if (!padrao) return 0;
    
    let pontos = 0;
    if (padrao.comportamento) pontos += 15;
    if (padrao.gatilho) pontos += 15;
    if (padrao.imagemIndesejada.descricao) pontos += 20;
    if (padrao.imagemIdeal.descricao) pontos += 20;
    if (padrao.imagemIdeal.checklist?.length >= 3) pontos += 10;
    if (padrao.repeticoes >= 5) pontos += 10;
    if (padrao.testado) pontos += 10;
    
    return pontos;
  };

  const padrao = padroes[padraoAtual];
  const progresso = calcularProgresso();

  if (!padrao) return null;

  return (
    <section id="pratica-swish" className="py-20 bg-gradient-to-br from-gray-50 to-purple-50">
      <div className="max-w-5xl mx-auto px-6">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Pratique a Técnica Swish
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Preencha cada etapa com atenção e execute o movimento SWISH para reprogramar seu padrão mental
          </p>
        </div>

        {/* Navegação entre padrões */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-2">
            {padroes.map((_, index) => (
              <button
                key={index}
                onClick={() => setPadraoAtual(index)}
                className={`w-10 h-10 rounded-full font-semibold transition-all duration-300 whitespace-nowrap ${
                  padraoAtual === index
                    ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg scale-110'
                    : 'bg-white text-gray-600 hover:bg-gray-100'
                }`}
              >
                {index + 1}
              </button>
            ))}
            {padroes.length < 5 && (
              <button
                onClick={adicionarNovoPadrao}
                className="w-10 h-10 rounded-full bg-white text-purple-600 hover:bg-purple-50 transition-all duration-300 border-2 border-purple-200 border-dashed whitespace-nowrap"
              >
                <i className="ri-add-line text-xl"></i>
              </button>
            )}
          </div>

          <button
            onClick={() => setShowExemplos(true)}
            className="px-4 py-2 bg-white text-purple-600 rounded-full text-sm font-medium hover:shadow-md transition-all duration-300 border border-purple-200 whitespace-nowrap"
          >
            <i className="ri-lightbulb-line mr-2"></i>
            Ver Exemplos
          </button>
        </div>

        {/* Barra de progresso */}
        <div className="bg-white rounded-2xl p-6 shadow-lg mb-8">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-semibold text-gray-700">Progresso do Padrão {padraoAtual + 1}</span>
            <span className="text-sm font-bold text-purple-600">{progresso}%</span>
          </div>
          <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-purple-600 to-pink-600 transition-all duration-500 rounded-full"
              style={{ width: `${progresso}%` }}
            ></div>
          </div>
        </div>

        {/* Dica aleatória */}
        <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl p-6 mb-8 border-2 border-amber-200">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl flex items-center justify-center flex-shrink-0">
              <i className="ri-lightbulb-flash-line text-xl text-white"></i>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-1">Dica Prática</h3>
              <p className="text-sm text-gray-700">{dicaAleatoria}</p>
            </div>
          </div>
        </div>

        {/* Formulário principal */}
        <div className="space-y-8">
          {/* Passo 1: Comportamento */}
          <div className="bg-white rounded-2xl p-8 shadow-lg">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
                <i className="ri-focus-3-line text-2xl text-white"></i>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">Passo 1: Identifique o Comportamento</h3>
                <p className="text-sm text-gray-600">O que você deseja mudar?</p>
              </div>
            </div>
            
            <textarea
              value={padrao.comportamento}
              onChange={(e) => atualizarPadrao('comportamento', e.target.value)}
              placeholder="Ex: Fico nervoso e travo quando preciso apresentar em público..."
              className="w-full h-24 px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-purple-400 focus:outline-none resize-none text-gray-700"
              maxLength={500}
            />
            <p className="text-xs text-gray-500 mt-2">{padrao.comportamento.length}/500 caracteres</p>
          </div>

          {/* Passo 2: Gatilho */}
          <div className="bg-white rounded-2xl p-8 shadow-lg">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-pink-500 to-rose-500 rounded-xl flex items-center justify-center">
                <i className="ri-flashlight-line text-2xl text-white"></i>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">Passo 2: Descubra o Gatilho Interno</h3>
                <p className="text-sm text-gray-600">O que passa pela sua mente antes disso acontecer?</p>
              </div>
            </div>

            <div className="bg-purple-50 rounded-xl p-4 mb-4">
              <p className="text-sm font-semibold text-purple-900 mb-2">Perguntas para ajudar:</p>
              <ul className="space-y-1">
                {perguntasGatilho.map((pergunta, index) => (
                  <li key={index} className="text-sm text-purple-700 flex items-start gap-2">
                    <i className="ri-arrow-right-s-line mt-0.5"></i>
                    <span>{pergunta}</span>
                  </li>
                ))}
              </ul>
            </div>
            
            <textarea
              value={padrao.gatilho}
              onChange={(e) => atualizarPadrao('gatilho', e.target.value)}
              placeholder="Ex: Imagino as pessoas me julgando e rindo de mim..."
              className="w-full h-24 px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-purple-400 focus:outline-none resize-none text-gray-700"
              maxLength={500}
            />
            <p className="text-xs text-gray-500 mt-2">{padrao.gatilho.length}/500 caracteres</p>
          </div>

          {/* Passo 3: Imagem Indesejada */}
          <div className="bg-white rounded-2xl p-8 shadow-lg">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-rose-500 to-orange-500 rounded-xl flex items-center justify-center">
                <i className="ri-image-line text-2xl text-white"></i>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">Imagem Mental Indesejada</h3>
                <p className="text-sm text-gray-600">Descreva a imagem que dispara o padrão negativo</p>
              </div>
            </div>
            
            <textarea
              value={padrao.imagemIndesejada.descricao}
              onChange={(e) => atualizarPadrao('imagemIndesejada.descricao', e.target.value)}
              placeholder="Ex: Vejo rostos críticos, sinto meu corpo tenso, ouço minha voz tremendo..."
              className="w-full h-32 px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-purple-400 focus:outline-none resize-none text-gray-700 mb-6"
              maxLength={500}
            />

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Tamanho</label>
                <select
                  value={padrao.imagemIndesejada.tamanho}
                  onChange={(e) => atualizarPadrao('imagemIndesejada.tamanho', e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-purple-400 focus:outline-none text-gray-700"
                >
                  <option value="grande">Grande</option>
                  <option value="media">Média</option>
                  <option value="pequena">Pequena</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Nitidez</label>
                <select
                  value={padrao.imagemIndesejada.nitidez}
                  onChange={(e) => atualizarPadrao('imagemIndesejada.nitidez', e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-purple-400 focus:outline-none text-gray-700"
                >
                  <option value="nitida">Nítida</option>
                  <option value="media">Média</option>
                  <option value="desfocada">Desfocada</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Proximidade</label>
                <select
                  value={padrao.imagemIndesejada.proximidade}
                  onChange={(e) => atualizarPadrao('imagemIndesejada.proximidade', e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-purple-400 focus:outline-none text-gray-700"
                >
                  <option value="proxima">Próxima</option>
                  <option value="media">Média</option>
                  <option value="distante">Distante</option>
                </select>
              </div>
            </div>

            <div className="mt-4 bg-rose-50 rounded-xl p-4 border border-rose-200">
              <p className="text-sm text-rose-800">
                <strong>Configuração ideal:</strong> Grande, Nítida e Próxima para máxima eficácia do Swish
              </p>
            </div>
          </div>

          {/* Passo 4: Imagem do EU IDEAL */}
          <div className="bg-white rounded-2xl p-8 shadow-lg">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-amber-500 rounded-xl flex items-center justify-center">
                <i className="ri-star-line text-2xl text-white"></i>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">Passo 3: Crie a Imagem do EU IDEAL</h3>
                <p className="text-sm text-gray-600">Como você gostaria de reagir nessa situação?</p>
              </div>
            </div>
            
            <textarea
              value={padrao.imagemIdeal.descricao}
              onChange={(e) => atualizarPadrao('imagemIdeal.descricao', e.target.value)}
              placeholder="Ex: Me vejo confiante, postura ereta, voz firme, público interessado e receptivo..."
              className="w-full h-32 px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-purple-400 focus:outline-none resize-none text-gray-700 mb-6"
              maxLength={500}
            />

            <div className="bg-amber-50 rounded-xl p-6 border border-amber-200">
              <h4 className="font-semibold text-gray-900 mb-4">Checklist do EU IDEAL:</h4>
              <div className="space-y-3">
                {checklistEuIdeal.map((item) => (
                  <label
                    key={item.id}
                    className="flex items-center gap-3 cursor-pointer group"
                  >
                    <input
                      type="checkbox"
                      checked={padrao.imagemIdeal.checklist?.includes(item.id) || false}
                      onChange={() => toggleChecklistItem(item.id)}
                      className="w-5 h-5 rounded border-2 border-gray-300 text-purple-600 focus:ring-2 focus:ring-purple-400 cursor-pointer"
                    />
                    <span className="text-gray-700 group-hover:text-gray-900 transition-colors">
                      {item.item}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* Passo 5: Movimento SWISH */}
          <div className="bg-gradient-to-br from-purple-600 to-pink-600 rounded-2xl p-8 shadow-2xl text-white">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                <i className="ri-speed-line text-2xl text-white"></i>
              </div>
              <div>
                <h3 className="text-2xl font-bold">Passo 5: Execute o Movimento SWISH</h3>
                <p className="text-purple-100">Clique no botão para cada repetição</p>
              </div>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 mb-6">
              <p className="text-white/90 leading-relaxed mb-4">
                <strong>Instruções:</strong> Visualize a imagem indesejada grande à sua frente. No canto inferior, coloque a imagem desejada pequena. Ao clicar, a imagem desejada cresce MUITO RÁPIDO e ocupa toda a tela, enquanto a indesejada desaparece. Faça mentalmente: "SWISHHH!"
              </p>
              
              <div className="flex items-center justify-between">
                <div className="text-center">
                  <div className="text-5xl font-bold mb-2">{contadorSwish}/10</div>
                  <div className="text-sm text-purple-100">Repetições</div>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={executarSwish}
                    disabled={contadorSwish >= 10 || animandoSwish}
                    className={`px-8 py-4 rounded-full font-bold text-lg transition-all duration-300 whitespace-nowrap ${
                      contadorSwish >= 10
                        ? 'bg-white/20 text-white/50 cursor-not-allowed'
                        : 'bg-white text-purple-600 hover:scale-105 hover:shadow-2xl'
                    }`}
                  >
                    {animandoSwish ? (
                      <span className="flex items-center gap-2">
                        <i className="ri-loader-4-line animate-spin"></i>
                        SWISHHH!
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <i className="ri-play-circle-line"></i>
                        Executar SWISH
                      </span>
                    )}
                  </button>

                  {contadorSwish > 0 && (
                    <button
                      onClick={resetarSwish}
                      className="px-6 py-4 bg-white/20 hover:bg-white/30 rounded-full font-semibold transition-all duration-300 whitespace-nowrap"
                    >
                      <i className="ri-restart-line mr-2"></i>
                      Resetar
                    </button>
                  )}
                </div>
              </div>
            </div>

            {contadorSwish >= 5 && (
              <div className="bg-emerald-500/20 backdrop-blur-sm rounded-xl p-4 border-2 border-emerald-300/50">
                <div className="flex items-center gap-3">
                  <i className="ri-checkbox-circle-line text-3xl text-emerald-200"></i>
                  <div>
                    <p className="font-semibold text-emerald-100">Ótimo trabalho!</p>
                    <p className="text-sm text-emerald-200">Você completou o mínimo de 5 repetições. Continue até 10 para máxima eficácia!</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Passo 8: Teste */}
          {contadorSwish >= 5 && (
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-xl flex items-center justify-center">
                  <i className="ri-checkbox-circle-line text-2xl text-white"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">Passo 8: Teste o Resultado</h3>
                  <p className="text-sm text-gray-600">Pense na situação original. Qual imagem surge primeiro?</p>
                </div>
              </div>

              <div className="space-y-4">
                <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200">
                  <p className="text-sm text-emerald-800 mb-3">
                    <strong>Como testar:</strong> Feche os olhos e pense na situação que costumava gerar o comportamento indesejado. Observe qual imagem mental aparece primeiro agora.
                  </p>
                </div>

                <textarea
                  value={padrao.resultado}
                  onChange={(e) => atualizarPadrao('resultado', e.target.value)}
                  placeholder="Descreva o que aconteceu quando você pensou na situação..."
                  className="w-full h-24 px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-emerald-400 focus:outline-none resize-none text-gray-700"
                  maxLength={500}
                />

                <button
                  onClick={() => atualizarPadrao('testado', true)}
                  className={`w-full px-6 py-4 rounded-xl font-semibold transition-all duration-300 whitespace-nowrap ${
                    padrao.testado
                      ? 'bg-emerald-500 text-white'
                      : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                  }`}
                >
                  {padrao.testado ? (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-checkbox-circle-fill"></i>
                      Teste Concluído
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-checkbox-circle-line"></i>
                      Marcar como Testado
                    </span>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Resumo final */}
        {progresso === 100 && (
          <div className="mt-8 bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-8 border-2 border-emerald-200">
            <div className="text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-trophy-line text-4xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Padrão Completo!</h3>
              <p className="text-gray-700 mb-6">
                Você concluiu todos os passos da Técnica Swish para este padrão. Continue praticando em situações reais para reforçar a reprogramação.
              </p>
              {padroes.length < 5 && (
                <button
                  onClick={adicionarNovoPadrao}
                  className="px-8 py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-full font-semibold hover:shadow-xl transition-all duration-300 hover:scale-105 whitespace-nowrap"
                >
                  <i className="ri-add-line mr-2"></i>
                  Criar Novo Padrão Swish
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Modal de exemplos */}
      {showExemplos && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-6" onClick={() => setShowExemplos(false)}>
          <div className="bg-white rounded-3xl max-w-4xl w-full p-8 shadow-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-3xl font-bold text-gray-900">Exemplos de Padrões Swish</h2>
              <button
                onClick={() => setShowExemplos(false)}
                className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
              >
                <i className="ri-close-line text-2xl text-gray-600"></i>
              </button>
            </div>

            <div className="grid gap-6">
              {situacoesExemplo.map((exemplo) => (
                <div
                  key={exemplo.id}
                  className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-6 border-2 border-purple-100 hover:border-purple-300 transition-all duration-300"
                >
                  <h3 className="text-xl font-bold text-gray-900 mb-4">{exemplo.titulo}</h3>
                  
                  <div className="space-y-3 mb-4">
                    <div>
                      <span className="text-sm font-semibold text-purple-600">Comportamento:</span>
                      <p className="text-gray-700">{exemplo.comportamento}</p>
                    </div>
                    <div>
                      <span className="text-sm font-semibold text-purple-600">Gatilho:</span>
                      <p className="text-gray-700">{exemplo.gatilho}</p>
                    </div>
                    <div>
                      <span className="text-sm font-semibold text-rose-600">Imagem Indesejada:</span>
                      <p className="text-gray-700">{exemplo.imagemIndesejada}</p>
                    </div>
                    <div>
                      <span className="text-sm font-semibold text-emerald-600">Imagem Ideal:</span>
                      <p className="text-gray-700">{exemplo.imagemIdeal}</p>
                    </div>
                  </div>

                  <button
                    onClick={() => aplicarExemplo(exemplo)}
                    className="w-full px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl font-semibold hover:shadow-lg transition-all duration-300 whitespace-nowrap"
                  >
                    <i className="ri-download-line mr-2"></i>
                    Usar Este Exemplo
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}